"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createRoleRequestSchema = exports.updateRoleRequestSchema = exports.rolesResponseSchema = exports.roleSchema = exports.fieldRuleSchema = exports.effectivePermissionsSchema = exports.fieldAccessSchema = exports.scopeSchema = exports.actionKeySchema = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
const catalogue_1 = require("./catalogue");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The wire shapes for the permission endpoints. Defined ONCE.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * CLAUDE.md §3: *"Every request and response shape is one schema, defined once; the server
 * validates with it and the client infers from it."*
 *
 * ⚠️ **`actionKeySchema` is an ENUM over the catalogue, not `z.string()`.** This is rule 8 at the
 * request boundary, and it is the same defect the `ActionKey` type fix caught one layer up: a
 * misspelled action in a request body would otherwise be stored happily, and a stored grant naming
 * an action nothing ever checks is a permission that reads as granted in the Jogosultságok screen
 * and is inert everywhere else. The screen would be *lying*, and nothing would fail.
 */
/* ⚠️ `z.enum` needs a non-empty tuple, and `ACTIONS` is a runtime array — so the cast states what
 * the catalogue's shape already guarantees. The alternative, `z.string().refine(...)`, loses the
 * enum from the inferred type and with it the compile-time check that makes this worth doing. */
exports.actionKeySchema = zod_1.z.enum(catalogue_1.ACTIONS);
exports.scopeSchema = zod_1.z.enum(catalogue_1.SCOPES);
exports.fieldAccessSchema = zod_1.z.enum(catalogue_1.FIELD_ACCESS);
/**
 * What `GET /permissions/me` returns: the caller's merged set.
 *
 * ⚠️ **`grants` and `fieldRules` are RECORDS, not arrays of pairs.** The client's hot path is
 * "what is my scope for `task.update`", called once per rendered control, and a record answers it
 * with a lookup rather than a scan.
 *
 * ⚠️ **The keys are NOT validated as an enum here, deliberately** — unlike a request body. A
 * server that has added an action the client's copy of the catalogue predates must not make the
 * client's parse fail: the client ignores keys it does not know, and every unknown action is
 * `none` by deny-by-default anyway. Strictness belongs on the way IN, tolerance on the way out.
 */
exports.effectivePermissionsSchema = zod_1.z.object({
    grants: zod_1.z.record(zod_1.z.string(), exports.scopeSchema),
    fieldRules: zod_1.z.record(zod_1.z.string(), exports.fieldAccessSchema),
});
/** One field rule, as the roles endpoint returns it. */
exports.fieldRuleSchema = zod_1.z.object({
    resource: zod_1.z.string(),
    field: zod_1.z.string(),
    access: exports.fieldAccessSchema,
});
/**
 * One role, with everything the Jogosultságok screen draws.
 *
 * ⚠️ **`builtin` and `version` are both load-bearing.** `builtin` is what the screen greys out —
 * `seed_builtin_roles` recreates the five per workspace, so an edit to one is a change the next
 * workspace does not get, which is a confusing half-feature rather than a customisation.
 * `version` is CLAUDE.md §3's stale-write guard: the screen sends it back and a mismatch is `409`.
 */
exports.roleSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `role_4d5e6f70` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('role'),
    code: zod_1.z.string(),
    name: zod_1.z.string(),
    description: zod_1.z.string().nullable(),
    builtin: zod_1.z.boolean(),
    version: zod_1.z.number().int(),
    /** `action` ⇒ scope. Absent action means `none`; the screen renders that as the empty column. */
    grants: zod_1.z.record(zod_1.z.string(), exports.scopeSchema),
    fieldRules: zod_1.z.array(exports.fieldRuleSchema),
    /** How many members hold it — the screen shows it, and a revoke needs it (`decisions.md` R-4). */
    memberCount: zod_1.z.number().int(),
});
exports.rolesResponseSchema = zod_1.z.object({
    /* ⚠️ `data` with no `meta.next_cursor`, and that is not an oversight of the list convention: a
     * workspace has five built-in roles plus a handful of custom ones, so paginating them would add
     * a cursor nobody advances. Said here so the next reader does not "fix" it. */
    data: zod_1.z.array(exports.roleSchema),
});
/**
 * A role edit.
 *
 * ⚠️ **The grant map is COMPLETE, not a patch — every action the sender wants granted is present,
 * and an omission means `none`.** A patch shape cannot express a revoke without a sentinel, and
 * `{ 'task.delete': 'none' }` versus omitting the key would then mean two different things that
 * look the same in a diff. The screen holds the whole matrix in memory anyway; it sends what it
 * shows.
 */
exports.updateRoleRequestSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(120).optional(),
    description: zod_1.z.string().max(500).nullable().optional(),
    /**
     * ⚠️ **`partialRecord`, NOT `record`, and this was a defect before it was a comment.**
     *
     * In Zod 4 `z.record(enum, value)` is **exhaustive**: it requires a key for *every* member of the
     * enum, and rejects anything less. Written as `z.record(actionKeySchema, scopeSchema)` this
     * schema demanded all twenty-five actions in every request — so the Jogosultsagok screen's save
     * would have failed for any role that does not grant literally everything, which is every role
     * except OWNER.
     *
     * It typechecked, because the inferred type was a complete `Record` and the only place that
     * type met a real object was a test. `z.partialRecord` keeps the enum validation — a misspelled
     * action is still refused, per rule 8 — and drops the exhaustiveness.
     *
     * ⚠️ An omitted action still means `none`; see the note below. Partial on the wire, complete in
     * meaning.
     */
    grants: zod_1.z.partialRecord(exports.actionKeySchema, exports.scopeSchema),
    fieldRules: zod_1.z.array(exports.fieldRuleSchema).default([]),
    /** ⚠️ Required, not optional. An optional version is a stale-write guard nobody triggers. */
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A NEW permission level.                                                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **NO GRANTS ON THE WAY IN, AND THAT IS THE DESIGN.** A level is created empty and then
 * edited on the screen that exists for editing levels. Taking a grant map here would mean two
 * places that write permissions, one of them a dialog with no room to show twenty-five actions
 * across four scopes — and the first thing anybody does after creating a level is open it anyway.
 *
 * ⚠️ **No `code` either.** `roles.code` is `null` for anything somebody created; the built-in five
 * own that column. A code typed into a dialog would look like an identifier the product respects,
 * and nothing reads it.
 *
 * ⚠️ **`builtin` is not on the wire at all** — it is the server's to set, always `false` here. A
 * client that could ask for a built-in role could make one nobody may delete.
 */
exports.createRoleRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(120),
    description: zod_1.z.string().trim().max(500).nullable().optional(),
    /**
     * ⚠️ **Copy another level's grants, optionally.** *"Like Member, plus contracts"* is how a real
     * workspace describes a new level, and starting from empty makes somebody tick twenty-five boxes
     * to get back to where they started. The id is validated against this workspace's own roles, so
     * it cannot reach another tenant's.
     */
    copyFromRoleId: zod_1.z.string().nullable().optional(),
});
//# sourceMappingURL=contract.js.map